import React, { useState, useRef, useEffect } from 'react';

const LeadsTable = ({ 
  leads = [], 
  loading = false, 
  selectedLeads = [], 
  onSelectLead, 
  onSelectAll, 
  onViewDetails, 
  onExport,
  userTier = 'free',
  searchTerm = '' // Add search term for highlighting
}) => {
  const [sortConfig, setSortConfig] = useState({ key: 'primary_business_name', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [, setHoveredRow] = useState(null);
  const [, setAnimationDelay] = useState(0);
  const [error, setError] = useState(null);
  const [, setRetryCount] = useState(0);
  const tableRef = useRef(null);

  // Keyboard navigation
  const [focusedRow, setFocusedRow] = useState(0);
  const [, setFocusedCol] = useState(0);

  // Enhanced keyboard shortcuts
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      // Ctrl/Cmd + A: Select all
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault();
        onSelectAll();
      }
      // Ctrl/Cmd + E: Export selected
      if ((e.ctrlKey || e.metaKey) && e.key === 'e') {
        e.preventDefault();
        if (selectedLeads.length > 0) {
          onExport('csv');
        }
      }
      // Escape: Clear selection
      if (e.key === 'Escape') {
        onSelectAll(); // This will clear if all are selected, or select all if none are selected
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [selectedLeads, onSelectAll, onExport]);

  // Error boundary functionality
  const handleRetry = () => {
    setError(null);
    setRetryCount(prev => prev + 1);
    // Trigger a re-fetch of data
    window.location.reload();
  };

  // Search highlighting function
  const highlightSearchTerm = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.toString().split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} style={styles.highlight}>
          {part}
        </mark>
      ) : part
    );
  };

  // Get accessible columns based on user tier
  const getAccessibleColumns = (tier) => {
    const baseColumns = [
      { key: 'primary_business_name', label: 'Business Name', sortable: true },
      { key: 'license_number', label: 'License Number', sortable: true },
      { key: 'total_installations', label: 'Total Installations', sortable: true }
    ];
    
    switch (tier) {
      case 'free':
        return baseColumns;
      case 'starter':
        return [
          ...baseColumns,
          { key: 'avg_system_size_kw', label: 'Avg System Size (kW)', sortable: true },
          { key: 'primary_service_city', label: 'Primary Service Area', sortable: true }
        ];
      case 'pro':
        return [
          ...baseColumns,
          { key: 'avg_system_size_kw', label: 'Avg System Size (kW)', sortable: true },
          { key: 'primary_service_city', label: 'Primary Service Area', sortable: true },
          { key: 'risk_score', label: 'Risk Score', sortable: true },
          { key: 'estimated_annual_revenue', label: 'Est. Annual Revenue', sortable: true }
        ];
      default:
        return baseColumns;
    }
  };

  const columns = getAccessibleColumns(userTier);

  // Sorting function
  const sortData = (data, key, direction) => {
    return [...data].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];

      // Handle null/undefined values
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';

      // Handle numeric values
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // Handle string values
      aVal = aVal.toString().toLowerCase();
      bVal = bVal.toString().toLowerCase();

      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Handle sort click
  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, rowIndex, colIndex) => {
    const maxRows = currentLeads.length - 1;
    const maxCols = columns.length + 2; // +2 for checkbox and actions columns

    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        setFocusedRow(Math.max(0, rowIndex - 1));
        break;
      case 'ArrowDown':
        e.preventDefault();
        setFocusedRow(Math.min(maxRows, rowIndex + 1));
        break;
      case 'ArrowLeft':
        e.preventDefault();
        setFocusedCol(Math.max(0, colIndex - 1));
        break;
      case 'ArrowRight':
        e.preventDefault();
        setFocusedCol(Math.min(maxCols, colIndex + 1));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (colIndex === 0) {
          // Checkbox column
          const leadId = currentLeads[rowIndex]?.business_id;
          if (leadId) onSelectLead(leadId);
        } else if (colIndex === maxCols - 1) {
          // Actions column
          const leadId = currentLeads[rowIndex]?.business_id;
          if (leadId) onViewDetails(leadId);
        }
        break;
      case 'Escape':
        setFocusedRow(0);
        setFocusedCol(0);
        break;
      default:
        break;
    }
  };

  // Format values for display
  const formatValue = (value, key) => {
    if (value === null || value === undefined) return '-';
    
    switch (key) {
      case 'total_installations':
      case 'installations_last_30_days':
      case 'installations_last_90_days':
      case 'installations_last_365_days':
        return value.toLocaleString();
      case 'avg_system_size_kw':
        return `${value.toFixed(1)} kW`;
      case 'avg_cost_per_watt':
        return `$${value.toFixed(2)}`;
      case 'risk_score':
        return value.toFixed(1);
      case 'estimated_annual_revenue':
        return value >= 1000000 ? `$${(value / 1000000).toFixed(1)}M` : `$${(value / 1000).toFixed(0)}K`;
      case 'approval_rate':
      case 'decommission_rate':
        return `${(value * 100).toFixed(1)}%`;
      default:
        return value;
    }
  };

  // Get tier badge color
  const getTierBadgeColor = (tier) => {
    switch (tier) {
      case 'FREE':
        return { bg: 'rgba(34, 197, 94, 0.2)', text: '#22C55E', border: 'rgba(34, 197, 94, 0.3)' };
      case 'STARTER':
        return { bg: 'rgba(59, 130, 246, 0.2)', text: '#3B82F6', border: 'rgba(59, 130, 246, 0.3)' };
      case 'PRO':
        return { bg: 'rgba(168, 85, 247, 0.2)', text: '#A855F7', border: 'rgba(168, 85, 247, 0.3)' };
      default:
        return { bg: 'rgba(107, 114, 128, 0.2)', text: '#6B7280', border: 'rgba(107, 114, 128, 0.3)' };
    }
  };

  // Pagination
  const sortedLeads = sortData(leads, sortConfig.key, sortConfig.direction);
  const totalPages = Math.ceil(sortedLeads.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentLeads = sortedLeads.slice(startIndex, endIndex);

  // Animation effects
  useEffect(() => {
    if (leads.length > 0) {
      setAnimationDelay(0);
    }
  }, [leads.length]);

  const styles = {
    container: {
      background: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '16px',
      overflow: 'hidden',
      backdropFilter: 'blur(10px)',
      transition: 'all 0.3s ease'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '20px 24px',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      background: 'rgba(255, 255, 255, 0.02)'
    },
    title: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#ffffff',
      margin: 0
    },
    actions: {
      display: 'flex',
      gap: '12px',
      alignItems: 'center'
    },
    actionButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 16px',
      background: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '8px',
      color: '#ffffff',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.15)',
        transform: 'translateY(-1px)'
      },
      '&:focus': {
        outline: '2px solid #3B82F6',
        outlineOffset: '2px'
      }
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    th: {
      padding: '16px 12px',
      textAlign: 'left',
      fontSize: '14px',
      fontWeight: '600',
      color: 'rgba(255, 255, 255, 0.8)',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      background: 'rgba(255, 255, 255, 0.02)',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.05)',
        color: '#ffffff'
      },
      '&:focus': {
        outline: '2px solid #3B82F6',
        outlineOffset: '2px'
      }
    },
    td: {
      padding: '16px 12px',
      fontSize: '14px',
      color: 'rgba(255, 255, 255, 0.9)',
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
      transition: 'all 0.2s ease'
    },
    row: {
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      animation: 'fadeInUp 0.5s ease forwards',
      animationDelay: '0ms'
    },
    rowHover: {
      background: 'rgba(255, 255, 255, 0.05)',
      transform: 'translateX(4px)'
    },
    rowSelected: {
      background: 'rgba(59, 130, 246, 0.1)',
      borderLeft: '3px solid #3B82F6',
      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)'
    },
    rowFocused: {
      background: 'rgba(59, 130, 246, 0.05)',
      outline: '2px solid #3B82F6',
      outlineOffset: '-2px'
    },
    checkbox: {
      width: '18px',
      height: '18px',
      accentColor: '#3B82F6',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:focus': {
        outline: '2px solid #3B82F6',
        outlineOffset: '2px'
      }
    },
    tierBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '4px 8px',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      transition: 'all 0.2s ease',
      '&:hover': {
        transform: 'scale(1.05)'
      }
    },
    pagination: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '20px 24px',
      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
      background: 'rgba(255, 255, 255, 0.02)'
    },
    paginationInfo: {
      fontSize: '14px',
      color: 'rgba(255, 255, 255, 0.7)'
    },
    paginationControls: {
      display: 'flex',
      gap: '8px',
      alignItems: 'center'
    },
    paginationButton: {
      padding: '8px 12px',
      background: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '6px',
      color: '#ffffff',
      fontSize: '14px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.15)',
        transform: 'translateY(-1px)'
      },
      '&:focus': {
        outline: '2px solid #3B82F6',
        outlineOffset: '2px'
      }
    },
    paginationButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.1)',
        transform: 'none'
      }
    },
    loadingOverlay: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '40px',
      color: 'rgba(255, 255, 255, 0.7)'
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '40px',
      color: 'rgba(255, 255, 255, 0.7)',
      animation: 'fadeIn 0.5s ease forwards'
    },
    emptyIcon: {
      fontSize: '48px',
      marginBottom: '16px',
      opacity: 0.5,
      animation: 'bounce 2s infinite'
    },
    skeletonRow: {
      height: '60px',
      background: 'linear-gradient(90deg, rgba(255,255,255,0.1) 25%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.1) 75%)',
      backgroundSize: '200% 100%',
      animation: 'loading 1.5s infinite',
      borderRadius: '4px',
      margin: '8px 0'
    },
    sortIcon: {
      fontSize: '12px',
      opacity: 0.6,
      transition: 'all 0.2s ease',
      marginLeft: '8px'
    },
    sortIconActive: {
      opacity: 1,
      color: '#3B82F6'
    },
    highlight: {
      backgroundColor: 'rgba(59, 130, 246, 0.3)',
      color: '#ffffff',
      padding: '0.2em 0.4em',
      borderRadius: '0.25em',
      fontWeight: 'bold'
    },
    errorBoundary: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '40px',
      color: 'rgba(255, 255, 255, 0.7)',
      animation: 'fadeIn 0.5s ease forwards'
    },
    errorIcon: {
      fontSize: '48px',
      marginBottom: '16px',
      opacity: 0.5,
      animation: 'bounce 2s infinite'
    },
    errorTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#ffffff',
      marginBottom: '8px'
    },
    errorMessage: {
      fontSize: '16px',
      color: 'rgba(255, 255, 255, 0.8)',
      textAlign: 'center',
      marginBottom: '20px',
      lineHeight: '1.5'
    },
    errorActions: {
      display: 'flex',
      gap: '12px',
      alignItems: 'center'
    },
    retryButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 16px',
      background: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '8px',
      color: '#ffffff',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.15)',
        transform: 'translateY(-1px)'
      },
      '&:focus': {
        outline: '2px solid #3B82F6',
        outlineOffset: '2px'
      }
    },
    reportButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 16px',
      background: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '8px',
      color: '#ffffff',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.15)',
        transform: 'translateY(-1px)'
      },
      '&:focus': {
        outline: '2px solid #3B82F6',
        outlineOffset: '2px'
      }
    },
    loadingState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '40px',
      color: 'rgba(255, 255, 255, 0.7)',
      animation: 'fadeIn 0.5s ease forwards'
    },
    loadingSpinner: {
      width: '60px',
      height: '60px',
      position: 'relative',
      marginBottom: '16px'
    },
    spinnerRing: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      width: '40px',
      height: '40px',
      margin: '-20px 0 0 -20px',
      border: '4px solid rgba(255, 255, 255, 0.2)',
      borderTop: '4px solid #3B82F6',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    },
    loadingTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#ffffff',
      marginBottom: '8px'
    },
    loadingSubtitle: {
      fontSize: '16px',
      color: 'rgba(255, 255, 255, 0.8)',
      marginBottom: '20px'
    },
    loadingProgress: {
      width: '100%',
      height: '8px',
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '4px',
      overflow: 'hidden',
      marginBottom: '10px'
    },
    progressBar: {
      height: '100%',
      background: '#3B82F6',
      borderRadius: '4px',
      width: '0%', // Will be updated by JS
      transition: 'width 0.3s ease-in-out'
    },
    progressFill: {
      height: '100%',
      background: '#3B82F6',
      borderRadius: '4px',
      width: '0%', // Will be updated by JS
      transition: 'width 0.3s ease-in-out'
    },
    progressText: {
      fontSize: '14px',
      color: 'rgba(255, 255, 255, 0.7)'
    },
    emptyTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#ffffff',
      marginBottom: '8px'
    },
    emptyMessage: {
      fontSize: '16px',
      color: 'rgba(255, 255, 255, 0.8)',
      textAlign: 'center',
      marginBottom: '20px',
      lineHeight: '1.5'
    },
    emptySuggestions: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '10px'
    },
    suggestionItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: 'rgba(255, 255, 255, 0.7)',
      fontSize: '14px'
    },
    suggestionIcon: {
      fontSize: '18px'
    }
  };

  // Error boundary component
  const ErrorBoundary = ({ error, onRetry }) => (
    <div style={styles.errorBoundary}>
      <div style={styles.errorIcon}>⚠️</div>
      <h3 style={styles.errorTitle}>Something went wrong</h3>
      <p style={styles.errorMessage}>
        {error?.message || 'Failed to load leads. Please try again.'}
      </p>
      <div style={styles.errorActions}>
        <button 
          style={styles.retryButton}
          onClick={onRetry}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="1 4 1 10 7 10"/>
            <polyline points="23 20 23 14 17 14"/>
            <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/>
          </svg>
          Retry
        </button>
        <button 
          style={styles.reportButton}
          onClick={() => window.open('mailto:support@huntorca.com?subject=Leads Table Error', '_blank')}
        >
          Report Issue
        </button>
      </div>
    </div>
  );

  // Enhanced loading component with progress
  const LoadingState = () => (
    <div style={styles.loadingState}>
      <div style={styles.loadingSpinner}>
        <div style={styles.spinnerRing}></div>
      </div>
      <h3 style={styles.loadingTitle}>Loading leads...</h3>
      <p style={styles.loadingSubtitle}>Fetching the latest data from our database</p>
      <div style={styles.loadingProgress}>
        <div style={styles.progressBar}>
          <div style={styles.progressFill}></div>
        </div>
        <span style={styles.progressText}>Processing...</span>
      </div>
    </div>
  );

  // Enhanced empty state with suggestions
  const EmptyState = () => (
    <div style={styles.emptyState}>
      <div style={styles.emptyIcon}>📊</div>
      <h3 style={styles.emptyTitle}>No leads found</h3>
      <p style={styles.emptyMessage}>
        {searchTerm 
          ? `No leads match "${searchTerm}". Try adjusting your search criteria.`
          : 'No leads available for your current filters. Try broadening your search.'
        }
      </p>
      <div style={styles.emptySuggestions}>
        <div style={styles.suggestionItem}>
          <span style={styles.suggestionIcon}>🔍</span>
          <span>Try different search terms</span>
        </div>
        <div style={styles.suggestionItem}>
          <span style={styles.suggestionIcon}>📍</span>
          <span>Expand your geographic area</span>
        </div>
        <div style={styles.suggestionItem}>
          <span style={styles.suggestionIcon}>📈</span>
          <span>Adjust installation count filters</span>
        </div>
      </div>
    </div>
  );

  // Show error state if there's an error
  if (error) {
    return (
      <div style={styles.container}>
        <ErrorBoundary error={error} onRetry={handleRetry} />
      </div>
    );
  }

  // Show enhanced loading state
  if (loading) {
    return (
      <div style={styles.container}>
        <LoadingState />
      </div>
    );
  }

  if (leads.length === 0) {
    return (
      <div style={styles.container}>
        <EmptyState />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Table Header */}
      <div style={styles.header}>
        <h3 style={styles.title}>
          {leads.length} Lead{leads.length !== 1 ? 's' : ''} Available
        </h3>
        <div style={styles.actions}>
          <button 
            style={styles.actionButton}
            onClick={() => onExport('csv')}
            disabled={selectedLeads.length === 0}
            aria-label={`Export ${selectedLeads.length} selected leads`}
          >
            📥 Export Selected ({selectedLeads.length})
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table 
          style={styles.table}
          ref={tableRef}
          role="grid"
          aria-label="Solar installer leads table"
        >
          <thead>
            <tr>
              <th style={styles.th}>
                <input
                  type="checkbox"
                  style={styles.checkbox}
                  checked={selectedLeads.length === currentLeads.length && currentLeads.length > 0}
                  onChange={onSelectAll}
                  aria-label="Select all leads"
                />
              </th>
              {columns.map(column => (
                <th 
                  key={column.key}
                  style={{
                    ...styles.th,
                    cursor: column.sortable ? 'pointer' : 'default'
                  }}
                  onClick={() => column.sortable && handleSort(column.key)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      column.sortable && handleSort(column.key);
                    }
                  }}
                  tabIndex={column.sortable ? 0 : -1}
                  role={column.sortable ? 'button' : undefined}
                  aria-label={column.sortable ? `Sort by ${column.label}` : undefined}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {column.label}
                    {column.sortable && (
                      <span 
                        style={{
                          ...styles.sortIcon,
                          ...(sortConfig.key === column.key ? styles.sortIconActive : {})
                        }}
                      >
                        {sortConfig.key === column.key ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
              <th style={styles.th}>Access Tier</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentLeads.map((lead, index) => {
              const isSelected = selectedLeads.includes(lead.business_id);
              const isFocused = focusedRow === index;
              const tierColors = getTierBadgeColor(lead.access_tier);
              
              return (
                <tr
                  key={lead.business_id}
                  style={{
                    ...styles.row,
                    ...(isSelected ? styles.rowSelected : {}),
                    ...(isFocused ? styles.rowFocused : {}),
                    animationDelay: `${index * 50}ms`
                  }}
                  onMouseEnter={() => setHoveredRow(lead.business_id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  onKeyDown={(e) => handleKeyDown(e, index, 0)}
                  tabIndex={0}
                  role="row"
                  aria-selected={isSelected}
                  aria-label={`Lead: ${lead.primary_business_name}`}
                >
                  <td style={styles.td}>
                    <input
                      type="checkbox"
                      style={styles.checkbox}
                      checked={isSelected}
                      onChange={() => onSelectLead(lead.business_id)}
                      aria-label={`Select ${lead.primary_business_name}`}
                    />
                  </td>
                  {columns.map((column, colIndex) => (
                    <td 
                      key={column.key} 
                      style={styles.td}
                      onKeyDown={(e) => handleKeyDown(e, index, colIndex + 1)}
                      tabIndex={0}
                    >
                      {highlightSearchTerm(formatValue(lead[column.key], column.key), searchTerm)}
                    </td>
                  ))}
                  <td style={styles.td}>
                    <span
                      style={{
                        ...styles.tierBadge,
                        background: tierColors.bg,
                        color: tierColors.text,
                        border: `1px solid ${tierColors.border}`
                      }}
                      role="status"
                      aria-label={`Access tier: ${lead.access_tier}`}
                    >
                      {lead.access_tier}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <button
                      style={{
                        ...styles.actionButton,
                        padding: '6px 12px',
                        fontSize: '12px'
                      }}
                      onClick={() => onViewDetails(lead.business_id)}
                      aria-label={`View details for ${lead.primary_business_name}`}
                    >
                      👁️ View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={styles.pagination}>
          <div style={styles.paginationInfo}>
            Showing {startIndex + 1}-{Math.min(endIndex, leads.length)} of {leads.length} leads
          </div>
          <div style={styles.paginationControls}>
            <button
              style={{
                ...styles.paginationButton,
                ...(currentPage === 1 ? styles.paginationButtonDisabled : {})
              }}
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label="Previous page"
            >
              ← Previous
            </button>
            <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              style={{
                ...styles.paginationButton,
                ...(currentPage === totalPages ? styles.paginationButtonDisabled : {})
              }}
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              aria-label="Next page"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: translateY(0);
          }
          40% {
            transform: translateY(-10px);
          }
          60% {
            transform: translateY(-5px);
          }
        }

        @keyframes loading {
          0% {
            background-position: 200% 0;
          }
          100% {
            background-position: -200% 0;
          }
        }
      `}</style>
    </div>
  );
};

export default LeadsTable; 